import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type {
  Driver,
  MastersFile,
  CsvImport,
  Statement,
  Carrier,
  MatchResult,
  ManualOverrides,
  ItemRow,
  ExtraDeduction,
  DeductionMasterItem,
  SagawaBenkindRule,
  SheetMaster,
  MonthlyRecord,
  MonthIndexEntry,
  ShidoRule,
  OfficeSumRule,
  Adjustment,
  GroupRule,
} from "./types";
import { isRetiredDriver } from "./lib/driverStatus";
import mastersJson from "./data/masters.json";
import { fetchSheetMaster } from "./lib/masterApi";
import { correctArchivedStatement, fetchMonthIndex, fetchMonthRecord, saveMonthRecord } from "./lib/archiveApi";
import { fetchWorkMeta, fetchWorkDoc, saveWorkDoc, type WorkMeta } from "./lib/workApi";
import { calcSagawaStatement } from "./lib/calc/sagawa";
import { calcSagawaShunanStatement, isShunanRow } from "./lib/calc/sagawaShunan";
import { calcYamatoStatement } from "./lib/calc/yamato";
import { mergeDriverStatement, finalizeStatement } from "./lib/calc/common";
import { buildGasLookup, applyGasToStatement } from "./lib/calc/gasDeduction";
import { DEFAULT_SAGAWA_BENKIND_RULES } from "./lib/calc/sagawaRules";
import {
  buildShidoItems,
  buildOfficeSumItems,
  floorAmount,
  DEFAULT_SHIDO_RULES,
  DEFAULT_OFFICE_SUM_RULES,
} from "./lib/calc/specialEarnings";
import { buildGroupItems, DEFAULT_GROUP_RULES } from "./lib/calc/groupSum";
import { groupDriverInvoiceNo } from "./lib/calc/groupInvoice";
import { matchDrivers } from "./lib/matching";
import { firstOfMonth, lastOfMonth, normalizeName } from "./lib/normalize";

const masters = mastersJson as MastersFile;

// localStorage 保存のキー（スキーマ変更時は v2, v3 と上げる）
const STORAGE_KEY = "hsd-monthly:state-v1";

export type DriverOverrides = {
  added: Driver[];
  updated: Record<string, Driver>; // id → 上書き Driver（差分でなく完成形を保持）
  deletedIds: string[];
};

const EMPTY_OVERRIDES: DriverOverrides = { added: [], updated: {}, deletedIds: [] };

// 控除項目マスターの初期値（全員共通の既定プルダウン項目 / Issue #2）
// 旧 StatementDetail のハードコード EXTRA_PRESETS と互換になる項目を初期登録する。
const DEFAULT_DEDUCTION_MASTER: DeductionMasterItem[] = [
  { id: "dm-gasoline", name: "ガソリン代", defaultAmount: null },
  { id: "dm-etc", name: "ETC代", defaultAmount: null },
  { id: "dm-highway", name: "高速代", defaultAmount: null },
  { id: "dm-maebori", name: "前借精算", defaultAmount: null },
  { id: "dm-bensho", name: "損害弁償", defaultAmount: null },
];

type State = {
  drivers: Driver[]; // シート連携時: sheetMaster.drivers / フォールバック時: masters.json + driverOverrides
  driverOverrides: DriverOverrides;
  // 正本マスターシートの最新取得結果（null = 未取得 or 未設定 → masters.json フォールバック）
  // localStorage に永続化＝「どの版のマスターで計算したか」の写し（R5）
  sheetMaster: SheetMaster | null;
  imports: Partial<Record<Carrier, CsvImport>>;
  targetMonth: string;
  statements: Statement[];
  matches: Partial<Record<Carrier, MatchResult[]>>;
  deductionMaster: DeductionMasterItem[]; // 控除項目マスター（全員共通）
  sagawaBenkindRules: SagawaBenkindRule[]; // 佐川 便種マッピング（#32・本城さんが設定画面で編集）
  shidoRules: ShidoRule[]; // 指導料設定（本城さん 6/30 確定情報・設定画面で編集）
  officeSumRules: OfficeSumRule[]; // 営業所コード集計（勝本様 73049 等）
  groupRules: GroupRule[]; // グループ集計（AT-STYLE様 等。docs/33・設定画面で編集）
  manualDriverIds: string[]; // 手動作成した通知書のドライバーID（CSVに出ない橋本様等。再計算でも維持）
  // 配信除外（本城さん要望 2026-08-10）: マスタに名前だけ登録され0円の通知書が出る人
  // （此原剛様・淺田範史様＝AT-STYLE経由で支払う人）を通知書一覧・配信から外す。
  // 0円のときだけ効く（金額が発生した月は通知書が自動復活し警告が出る＝支払が黙って消えない）。
  excludedDriverIds: string[];
  // ===== 確定した月のロック（本城さん要望 2026-09-01）=====
  // 確定（スナップショット保存）した月は、そのままだと編集できてしまい、
  // 画面の数字と保存済みスナップショットが黙ってズレる。支払額が変わったのに
  // 連絡が漏れるリスクがあるため、「確定＝編集不可、訂正すると決めたときだけ解除」にする。
  finalizedMonths: string[]; // サーバに保存済みの月（月インデックスの写し。Provider が同期する）
  editUnlocked: boolean; // 訂正モード（確定済みの月をあえて編集可能にした状態）
  unlockedBy: string | null; // 誰が訂正を始めたか
  unlockedAt: string | null; // いつ訂正を始めたか
};

export type Action =
  | { type: "SET_TARGET_MONTH"; month: string }
  | { type: "ADD_IMPORT"; imp: CsvImport }
  | { type: "REMOVE_IMPORT"; carrier: Carrier }
  | { type: "UPDATE_STATEMENT"; stmt: Statement }
  | { type: "SET_MANUAL_OVERRIDES"; driverId: string; overrides: ManualOverrides }
  | { type: "SET_EXTRA_DEDUCTIONS"; driverId: string; deductions: ExtraDeduction[] }
  | { type: "SET_SHEET_MASTER"; data: SheetMaster }
  | { type: "ADD_DRIVER"; driver: Driver }
  | { type: "UPDATE_DRIVER"; driver: Driver }
  | { type: "DELETE_DRIVER"; driverId: string }
  | { type: "ADD_DEDUCTION_ITEM"; item: DeductionMasterItem }
  | { type: "UPDATE_DEDUCTION_ITEM"; item: DeductionMasterItem }
  | { type: "DELETE_DEDUCTION_ITEM"; id: string }
  | { type: "ADD_SAGAWA_RULE"; rule: SagawaBenkindRule }
  | { type: "UPDATE_SAGAWA_RULE"; rule: SagawaBenkindRule }
  | { type: "DELETE_SAGAWA_RULE"; code: string }
  | { type: "SET_SHIDO_RULES"; rules: ShidoRule[] }
  | { type: "SET_OFFICE_SUM_RULES"; rules: OfficeSumRule[] }
  | { type: "SET_GROUP_RULES"; rules: GroupRule[] }
  // グループへメンバーを1名追加（入社フォーム用）。rules 配列を丸ごと差し替えると、
  // 設定画面が開いたまま（＝古いコピーを持ったまま）のときに、その画面の保存で
  // 追加した人が静かに消えるため、reducer 内で「現在の」state に対して足す。
  | { type: "ADD_GROUP_MEMBER"; ruleId: string; name: string }
  | { type: "ADD_MANUAL_STATEMENT"; driverId: string }
  | { type: "REMOVE_MANUAL_STATEMENT"; driverId: string }
  | { type: "EXCLUDE_DRIVER"; driverId: string }
  | { type: "RESTORE_EXCLUDED_DRIVER"; driverId: string }
  | { type: "SET_ADJUSTMENTS"; driverId: string; adjustments: Adjustment[] }
  | { type: "LOAD_ARCHIVE_FOR_EDIT"; record: MonthlyRecord; by?: string }
  | { type: "LOAD_SHARED"; payload: SharedWork }
  | { type: "CLEAR_ALL" }
  | { type: "SET_FINALIZED_MONTHS"; months: string[] }
  | { type: "UNLOCK_MONTH"; by: string }
  | { type: "RELOCK_MONTH" }
  | { type: "HYDRATE"; partial: Partial<State> };

// 確定した月のロック判定（本城さん要望 2026-09-01）。
// 「保存済みの月」を開いていて、かつ訂正モードに入っていなければロック。
// 前回の明細から確定状態・手入力を引き継いでよいか（Fableレビュー 2026-09-01）。
// 引き継ぐのは「同じ月を計算し直したとき」だけ。月が変わる取込で引き継ぐと、前月の臨時控除
// （前借精算等）が当月に複製され、確定済みの印まで持ち越されて支払額が実際に狂う。
// targetMonth を持たない古い保存データは、従来どおり引き継ぐ（判定できないため）。
export function canCarryOverPrev(prev: { targetMonth?: string }, month: string): boolean {
  return !prev.targetMonth || prev.targetMonth === month;
}

export function isMonthLocked(s: Pick<State, "finalizedMonths" | "targetMonth" | "editUnlocked">): boolean {
  return s.finalizedMonths.includes(s.targetMonth) && !s.editUnlocked;
}

// 月が替わる取込（翌月のCSVをアップロード）で捨てる「その月だけの作業状態」。
//   - 訂正モード: 無関係な月に editUnlocked が残り、共有経由で全端末に配られる（Fableレビュー M-A）
//   - 手動作成の通知書（橋本様等）: 残すと翌月にも売上0円の通知書が自動で作られ、月額控除だけが
//     載った明細が確定・発行されうる（Codex レビュー P1 2026-09-07。「全消去」ボタン廃止で
//     この経路が唯一の翌月開始手段になったため、CLEAR_ALL と同じく作業データとして消す）
// 同じ月のCSVの入れ直しでは何も捨てない。
export function monthRolloverPatch(state: Pick<State, "targetMonth">, newMonth: string): Partial<State> {
  if (newMonth === state.targetMonth) return {};
  return { editUnlocked: false, unlockedBy: null, unlockedAt: null, manualDriverIds: [] };
}

// ロック中は通さない編集操作。金額・確定状態・通知書の増減を変えるものだけを挙げる
// （閲覧・PDF出力・承認用の発行は含めない＝内容を変えないため）。
const LOCKED_ACTIONS: ReadonlySet<Action["type"]> = new Set([
  "UPDATE_STATEMENT",
  "SET_MANUAL_OVERRIDES",
  "SET_EXTRA_DEDUCTIONS",
  "SET_ADJUSTMENTS",
  "ADD_MANUAL_STATEMENT",
  "REMOVE_MANUAL_STATEMENT",
  "EXCLUDE_DRIVER",
  "RESTORE_EXCLUDED_DRIVER",
  "SET_TARGET_MONTH",
  // 取込済みCSVを外すと明細の作り直しになる。確定した月では外せない
  // （次の月のCSVをアップロードすれば、そちらへ切り替わる）
  "REMOVE_IMPORT",
]);

// masters.json + driverOverrides を合成して最終的なドライバー一覧を作る
function mergeDrivers(overrides: DriverOverrides): Driver[] {
  const deleted = new Set(overrides.deletedIds);
  const result: Driver[] = [];
  for (const d of masters.drivers) {
    if (deleted.has(d.id)) continue;
    const upd = overrides.updated[d.id];
    result.push(upd ?? d);
  }
  for (const a of overrides.added) {
    if (deleted.has(a.id)) continue;
    result.push(a);
  }
  return result;
}

// ドライバー一覧の最終形を決める：
//   シート連携中はシートが正本（ローカル overrides は適用しない＝シートと画面が常に一致）
//   未接続時は従来どおり masters.json + overrides
function computeDrivers(sheetMaster: SheetMaster | null, overrides: DriverOverrides): Driver[] {
  if (sheetMaster) return sheetMaster.drivers;
  return mergeDrivers(overrides);
}

// 次の追加用 ID を採番（D99001 〜 を使い、既存と衝突しないように）
export function nextDriverId(drivers: Driver[]): string {
  let n = 99001;
  const used = new Set(drivers.map((d) => d.id));
  while (used.has(`D${String(n).padStart(5, "0")}`)) n++;
  return `D${String(n).padStart(5, "0")}`;
}

// 臨時控除を items に反映してサマリーを再集計
// extraDeductions は category="deduct" の items として展開する。
// （MF送信時にそのまま明細行として扱えるようにするため）
// 再集計を冪等にするため、既存の note="__extra__" 行を一度すべて削除してから足し直す。
export function applyExtraDeductions(stmt: Statement, deductions: ExtraDeduction[]): Statement {
  const cleaned = stmt.items.filter((it) => it.note !== "__extra__");
  const extras: ItemRow[] = deductions
    .filter((d) => d.amount > 0 && d.name.trim() !== "")
    .map((d) => ({
      deliveryDate: stmt.targetMonth + "-01",
      name: d.name,
      unitPrice: -Math.abs(d.amount),
      quantity: 1,
      amount: -Math.abs(d.amount),
      // 課税/不課税は行ごとに選択（本城さん指示 6/19）。旧データは taxRate 無し → 不課税
      taxRate: d.taxRate ?? "non",
      note: "__extra__",
      // 備考は調整・臨時売上と同じ detail に載せる（通知書・承認ページ・スナップショットで同じ経路で出る）
      detail: d.detail?.trim() || undefined,
      category: "deduct" as const,
    }));
  const items = [...cleaned, ...extras];
  // 集計・値引き再導出は finalizeStatement に一元化（課税の臨時控除は値引き基準に効く）
  return finalizeStatement({ ...stmt, items, extraDeductions: deductions });
}

// 調整・臨時売上を items に反映（売上側の手入力行。橋本様・代走調整）
// 冪等にするため note="__adjust__" 行を一度すべて削除してから足し直す。
export function applyAdjustments(stmt: Statement, adjustments: Adjustment[]): Statement {
  const cleaned = stmt.items.filter((it) => it.note !== "__adjust__");
  const rows: ItemRow[] = adjustments
    .filter((a) => a.name.trim() !== "" && Number.isFinite(a.unitPrice) && Number.isFinite(a.quantity) && a.quantity !== 0)
    .map((a) => ({
      deliveryDate: stmt.salesDate,
      name: a.name,
      unitPrice: a.unitPrice,
      quantity: a.quantity,
      amount: floorAmount(a.unitPrice, a.quantity),
      taxRate: a.taxRate,
      note: "__adjust__",
      detail: a.detail,
      category: "sales" as const,
    }));
  const items = [...cleaned, ...rows];
  return finalizeStatement({ ...stmt, items, adjustments });
}

// 手入力単価を items に反映してサマリーを再集計
export function applyManualOverrides(stmt: Statement, overrides: ManualOverrides): Statement {
  const items: ItemRow[] = stmt.items.map((it) => {
    if (it.category !== "manual") return it;
    if (it.name === "ガソリンカード利用料") {
      const price = overrides.gasoline;
      if (price == null) return { ...it, unitPrice: null, amount: 0 };
      return { ...it, unitPrice: -Math.abs(price), amount: -Math.abs(price) * it.quantity };
    }
    if (it.name === "指導料") {
      const price = overrides.shido;
      if (price == null) return { ...it, unitPrice: null, amount: 0 };
      return { ...it, unitPrice: -Math.abs(price), amount: -Math.abs(price) * it.quantity };
    }
    return it;
  });
  // 集計・値引き再導出は finalizeStatement に一元化（指導料=課税10%は値引き基準に効く）
  return finalizeStatement({ ...stmt, items, manualOverrides: overrides });
}

function defaultTargetMonth(imports: State["imports"]): string {
  for (const carrier of ["sagawa", "yamato"] as Carrier[]) {
    const imp = imports[carrier];
    if (imp?.meta.periodStart) return imp.meta.periodStart.substring(0, 7);
  }
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function recalc(state: State): State {
  // 確定した月は再計算しない（本城さん要望 2026-09-01）。
  // 設定変更・マスター再取得で確定済みの金額が黙って動くのを、計算の入口で止める。
  // 別の月のCSVを取り込んだ場合は targetMonth が先に切り替わるため、ここには掛からない。
  if (isMonthLocked(state)) return state;
  const month = state.targetMonth;
  const monthFirst = firstOfMonth(month);
  const statements: Statement[] = [];
  const matches: State["matches"] = {};
  const gasLookup = buildGasLookup(state.sheetMaster, month);
  // 既存の確定状態・確定の記録・手入力を引き継ぐための index（R1: 人単位1通なので driverId キー）
  type PrevInfo = {
    status: Statement["status"];
    confirmedAt?: string | null;
    confirmedBy?: string | null;
    overrides?: ManualOverrides;
    extras?: ExtraDeduction[];
    adjustments?: Adjustment[];
  };
  const prevByDriver = new Map<string, PrevInfo>();
  for (const s of state.statements) {
    // 別の月の明細から引き継がない（Fableレビュー 2026-09-01 の指摘）。
    // 引き継ぐのは「同じ月を計算し直したとき」だけ。月が変わる取込で引き継ぐと、
    // 前月の臨時控除（前借精算等）が当月に複製され、確定済みの印まで持ち越されて
    // 支払額が実際に狂う。targetMonth を持たない古いデータは従来どおり引き継ぐ。
    if (!canCarryOverPrev(s, month)) continue;
    prevByDriver.set(s.driverId, {
      status: s.status,
      confirmedAt: s.confirmedAt,
      confirmedBy: s.confirmedBy,
      overrides: s.manualOverrides,
      extras: s.extraDeductions,
      adjustments: s.adjustments,
    });
  }

  // 業者ごとに突合し、売上itemsを driverId 単位で集約する（控除はここでは付けない）
  type Raw = {
    driver: Driver;
    salesItems: ItemRow[];
    carriers: Set<Carrier>;
    specialRule: string | null;
    warnings: string[];
  };
  const byDriver = new Map<string, Raw>();

  for (const carrier of ["sagawa", "yamato"] as Carrier[]) {
    const imp = state.imports[carrier];
    if (!imp) continue;

    const seen = new Map<string, { name: string; nameNormalized: string; code: string }>();
    if (carrier === "sagawa" && imp.sagawaRows) {
      for (const r of imp.sagawaRows) {
        if (!seen.has(r.driverName)) {
          seen.set(r.driverName, { name: r.driverName, nameNormalized: r.driverNameNormalized, code: r.driverCode });
        }
      }
    } else if (carrier === "yamato" && imp.yamatoRows) {
      for (const r of imp.yamatoRows) {
        if (!seen.has(r.driverName)) {
          seen.set(r.driverName, { name: r.driverName, nameNormalized: r.driverNameNormalized, code: r.driverCode });
        }
      }
    }
    const csvNames = [...seen.values()];

    const mr = matchDrivers(csvNames, state.drivers, monthFirst);
    matches[carrier] = mr;

    for (const m of mr) {
      let driverId: string | null = m.driverId;
      if (!driverId && m.status === "candidate" && m.candidates.length === 1) {
        driverId = m.candidates[0].id;
      }
      if (!driverId) continue;
      const driver = state.drivers.find((d) => d.id === driverId);
      if (!driver) continue;

      // 業者別に売上を計算（戻り値 Statement の売上itemsだけ使う。控除はあとで人単位に1回付ける）
      let cstmt: Statement;
      if (carrier === "sagawa") {
        const rows = imp.sagawaRows!.filter((r) => r.driverName === m.csvName);
        // 周南営業所のドライバーは特殊計上ロジックを使う（Issue #3）
        const isShunan = rows.some((r) => isShunanRow(r));
        cstmt = isShunan
          ? calcSagawaShunanStatement(driver, rows, month, state.sagawaBenkindRules)
          : calcSagawaStatement(driver, rows, month, state.sagawaBenkindRules);
      } else {
        const rows = imp.yamatoRows!.filter((r) => r.driverName === m.csvName);
        cstmt = calcYamatoStatement(driver, rows, month);
      }

      let raw = byDriver.get(driverId);
      if (!raw) {
        raw = { driver, salesItems: [], carriers: new Set(), specialRule: null, warnings: [] };
        byDriver.set(driverId, raw);
      }
      raw.carriers.add(carrier);
      for (const it of cstmt.items) {
        if (it.category === "sales") raw.salesItems.push(it);
      }
      if (cstmt.specialRule) raw.specialRule = cstmt.specialRule;
      for (const w of cstmt.warnings) if (!raw.warnings.includes(w)) raw.warnings.push(w);
    }
  }

  // ── 特殊計上（指導料・営業所コード集計）を売上itemとして合流させる（本城さん 6/30 確定情報） ──
  const yRowsAll = state.imports.yamato?.yamatoRows ?? [];
  const sRowsAll = state.imports.sagawa?.sagawaRows ?? [];
  const monthLast = lastOfMonth(month);
  const shidoInstructors = new Set<string>(); // 正規化氏名（manual指導料枠の抑止に使う）
  if (yRowsAll.length + sRowsAll.length > 0) {
    for (const rule of state.shidoRules) {
      const instructor = state.drivers.find(
        (d) => d.nameNormalized === normalizeName(rule.instructorName) && !isRetiredDriver(d)
      );
      if (!instructor) continue;
      const items = buildShidoItems(rule, yRowsAll, sRowsAll, state.sagawaBenkindRules, monthLast);
      if (items.length === 0) continue;
      shidoInstructors.add(instructor.nameNormalized);
      let raw = byDriver.get(instructor.id);
      if (!raw) {
        raw = { driver: instructor, salesItems: [], carriers: new Set(), specialRule: null, warnings: [] };
        byDriver.set(instructor.id, raw);
      }
      raw.salesItems.push(...items);
    }
    for (const rule of state.officeSumRules) {
      const driver = state.drivers.find(
        (d) => d.nameNormalized === normalizeName(rule.driverName) && !isRetiredDriver(d)
      );
      if (!driver) continue;
      const items = buildOfficeSumItems(rule, yRowsAll, monthLast);
      if (items.length === 0) continue;
      let raw = byDriver.get(driver.id);
      if (!raw) {
        raw = { driver, salesItems: [], carriers: new Set(), specialRule: null, warnings: [] };
        byDriver.set(driver.id, raw);
      }
      raw.carriers.add("yamato");
      raw.salesItems.push(...items);
    }
  }

  // ── グループ集計（AT-STYLE様 等。docs/33）：メンバーのCSV個数をグループ専用単価で集計し、
  //    グループ宛の通知書1枚を作る。合成 driverId "group:<ruleId>" で人単位1通の流儀に乗せる。 ──
  if (yRowsAll.length + sRowsAll.length > 0) {
    for (const rule of state.groupRules) {
      const items = buildGroupItems(rule, state.drivers, yRowsAll, sRowsAll, state.sagawaBenkindRules, monthLast);
      if (items.length === 0) continue; // 対象メンバーがCSV/マスタどちらにも見つからない
      const groupDriverId = `group:${rule.id}`;
      const groupDriver: Driver = {
        id: groupDriverId,
        name: rule.groupName,
        nameNormalized: normalizeName(rule.groupName),
        org: rule.groupName, // noticeRecipient が org を見て「会社らしければ御中」を判定する
        branch: "グループ集計",
        contractFrom: null,
        contractTo: null,
        unitTakkyu: null,
        unitNekopos: null,
        unitDm: null,
        // 登録番号（本城さん要望 2026-08-26「AT-STYLE様の通知書にインボイス番号が表示されていない」）。
        // 設定画面の入力 →（無ければ）マスタで所属がグループ名と一致する行の番号、の順に採用する。
        // どちらも無い場合はダミー "GROUP"（従来どおり無表示）。空文字にすると
        // インボイス値引き（buildInvoiceDiscount）が発動して金額が変わるため、必ず何かを入れる。
        invoiceNo: groupDriverInvoiceNo(rule, state.drivers),
        contractDocOk: true,
        monthlyKeitai: null,
        monthlySharyo: null,
        hasGasCard: false,
        gasUsage: null,
        monthlyAppli: null,
        shidoCount: null,
      };
      const carriers = new Set<Carrier>(items.map((it) => it.carrier).filter((c): c is Carrier => !!c));
      byDriver.set(groupDriverId, { driver: groupDriver, salesItems: items, carriers, specialRule: "グループ集計", warnings: [] });
    }
  }

  // ── 手動作成の通知書（CSVに出ないドライバー。橋本様等）を確保する ──
  // 売上itemは空で作り、調整・臨時売上（adjustments）は下の引き継ぎで再適用される。
  for (const id of state.manualDriverIds) {
    if (byDriver.has(id)) continue;
    const driver = state.drivers.find((d) => d.id === id);
    if (!driver) continue;
    byDriver.set(id, { driver, salesItems: [], carriers: new Set(), specialRule: null, warnings: [] });
  }

  // driver 単位に統合 → 1通の通知書を作る（控除・インボイスは1回だけ＝R1 二重計上の構造的解消）
  for (const [driverId, raw] of byDriver) {
    const carriers = (["sagawa", "yamato"] as Carrier[]).filter((c) => raw.carriers.has(c));
    let stmt: Statement = mergeDriverStatement(raw.driver, raw.salesItems, carriers, month, {
      specialRule: raw.specialRule,
      warnings: raw.warnings,
    });
    if (driverId.startsWith("group:")) {
      stmt.groupId = driverId.slice("group:".length);
    }

    // 指導料設定がある指導者は、旧・手入力式の指導料枠（マスター件数連動のmanual行）を出さない
    // （指導料は売上側の自動計上に置き換わったため。設定が無い人は従来どおり）
    if (shidoInstructors.has(raw.driver.nameNormalized)) {
      stmt = finalizeStatement({
        ...stmt,
        items: stmt.items.filter((it) => !(it.category === "manual" && it.name === "指導料")),
      });
    }

    // 確定状態・確定の記録・手入力を引き継ぐ（driverId 単位）
    // 確定/保留 は手動操作なので、再計算（マスター更新・再取込）後も維持する。
    const prev = prevByDriver.get(driverId);
    if (prev && (prev.status === "confirmed" || prev.status === "hold")) {
      stmt.status = prev.status;
      stmt.confirmedAt = prev.confirmedAt ?? null;
      stmt.confirmedBy = prev.confirmedBy ?? null;
    }
    if (prev?.overrides && Object.keys(prev.overrides).length > 0) {
      stmt = applyManualOverrides(stmt, prev.overrides);
    }
    if (prev?.extras && prev.extras.length > 0) {
      stmt = applyExtraDeductions(stmt, prev.extras);
    }
    if (prev?.adjustments && prev.adjustments.length > 0) {
      stmt = applyAdjustments(stmt, prev.adjustments);
    }
    // 月別控除入力シートのガソリン金額を反映（手入力済みなら手入力が優先）。
    // 「ガソリンカード貸与」フラグが空でも、当月の実額があれば控除行を追加して反映する（#29）。
    const gasAmount = gasLookup
      ? gasLookup.get(driverId) ?? gasLookup.get(raw.driver.nameNormalized)
      : undefined;
    if (gasAmount != null) {
      stmt = applyGasToStatement(stmt, gasAmount);
    }

    // 配信除外（本城さん要望 2026-08-10）: 0円のままなら通知書を作らない。
    // 金額が入っているのに除外設定されている場合は、支払が黙って消えるのを防ぐため
    // 通知書を残して警告を出す（フラグではなく実データで判定）。手動作成は本人の意思なので除外しない。
    if (state.excludedDriverIds.includes(driverId) && !state.manualDriverIds.includes(driverId)) {
      const isZero = stmt.salesTotal === 0 && stmt.deductTotal === 0 && stmt.netTotal === 0;
      if (isZero) continue;
      stmt = {
        ...stmt,
        warnings: [
          ...stmt.warnings,
          "配信除外に設定されていますが金額が発生しているため、通知書を作成しました（除外の解除または金額を確認してください）",
        ],
      };
    }
    statements.push(stmt);
  }

  return { ...state, statements, matches };
}

// ロック中に通してはいけない操作か（本城さん要望 2026-09-01）。
// ボタンを disabled にするだけでは別経路から書けてしまうため、状態を変える入口で判定する。
export function isBlockedWhileLocked(
  state: Pick<State, "finalizedMonths" | "targetMonth" | "editUnlocked">,
  action: Action
): boolean {
  if (action.type === "ADD_IMPORT") {
    // 取込は「いま開いている月」ではなく「CSVの月」で判定する（Fableレビュー 2026-09-01 の指摘）。
    // 現在月だけを見ていると、未確定の月を開いた状態で確定済みの月のCSVを入れられてしまい、
    // 対象月だけ切り替わって明細は前の月のまま、という矛盾した状態が無警告でできる。
    const m = action.imp.meta.periodStart ? action.imp.meta.periodStart.substring(0, 7) : null;
    if (!m) return isMonthLocked(state); // 対象期間が読めないCSVは、ロック中なら入れさせない
    if (state.editUnlocked && m === state.targetMonth) return false; // 訂正中の月は入れ直せる
    return state.finalizedMonths.includes(m); // 確定済みの月のCSVは入れさせない
  }
  if (!isMonthLocked(state)) return false;
  return LOCKED_ACTIONS.has(action.type);
}

function reducer(state: State, action: Action): State {
  if (isBlockedWhileLocked(state, action)) return state;
  switch (action.type) {
    case "SET_TARGET_MONTH":
      return recalc({ ...state, targetMonth: action.month });
    case "ADD_IMPORT": {
      const imports = { ...state.imports, [action.imp.meta.carrier]: action.imp };
      const newMonth = action.imp.meta.periodStart
        ? action.imp.meta.periodStart.substring(0, 7)
        : state.targetMonth;
      // 月が替わる＝前月の作業の終わり。訂正モードと手動通知書を持ち越さない（monthRolloverPatch 参照）
      return recalc({ ...state, imports, targetMonth: newMonth, ...monthRolloverPatch(state, newMonth) });
    }
    case "REMOVE_IMPORT": {
      const imports = { ...state.imports };
      delete imports[action.carrier];
      return recalc({ ...state, imports });
    }
    case "UPDATE_STATEMENT": {
      // R1: 人単位1通なので driverId で一意
      const statements = state.statements.map((s) =>
        s.driverId === action.stmt.driverId ? action.stmt : s
      );
      return { ...state, statements };
    }
    case "SET_MANUAL_OVERRIDES": {
      const statements = state.statements.map((s) => {
        if (s.driverId === action.driverId) {
          return applyManualOverrides(s, action.overrides);
        }
        return s;
      });
      return { ...state, statements };
    }
    case "SET_EXTRA_DEDUCTIONS": {
      const statements = state.statements.map((s) => {
        if (s.driverId === action.driverId) {
          return applyExtraDeductions(s, action.deductions);
        }
        return s;
      });
      return { ...state, statements };
    }
    case "SET_SHEET_MASTER": {
      const drivers = computeDrivers(action.data, state.driverOverrides);
      // 取込CSVが無いとき（再オープン中など）に recalc すると明細が全消えするため、
      // ドライバー一覧の更新だけ行う（明細はスナップショットのまま）
      if (Object.keys(state.imports).length === 0) return { ...state, sheetMaster: action.data, drivers };
      return recalc({ ...state, sheetMaster: action.data, drivers });
    }
    case "ADD_DRIVER": {
      const overrides: DriverOverrides = {
        ...state.driverOverrides,
        added: [...state.driverOverrides.added, action.driver],
        // 削除リストから復活させる（万一同じIDが含まれていれば）
        deletedIds: state.driverOverrides.deletedIds.filter((id) => id !== action.driver.id),
      };
      const drivers = computeDrivers(state.sheetMaster, overrides);
      return recalc({ ...state, drivers, driverOverrides: overrides });
    }
    case "UPDATE_DRIVER": {
      const d = action.driver;
      // masters.json に存在する ID なら updated に積む。追加分なら added 配列を上書き
      const inMasters = masters.drivers.some((m) => m.id === d.id);
      let overrides: DriverOverrides;
      if (inMasters) {
        overrides = {
          ...state.driverOverrides,
          updated: { ...state.driverOverrides.updated, [d.id]: d },
        };
      } else {
        overrides = {
          ...state.driverOverrides,
          added: state.driverOverrides.added.map((a) => (a.id === d.id ? d : a)),
        };
      }
      const drivers = computeDrivers(state.sheetMaster, overrides);
      return recalc({ ...state, drivers, driverOverrides: overrides });
    }
    case "DELETE_DRIVER": {
      const id = action.driverId;
      const inMasters = masters.drivers.some((m) => m.id === id);
      let overrides: DriverOverrides;
      if (inMasters) {
        overrides = {
          ...state.driverOverrides,
          deletedIds: state.driverOverrides.deletedIds.includes(id)
            ? state.driverOverrides.deletedIds
            : [...state.driverOverrides.deletedIds, id],
          // 念のため updated からも消す
          updated: Object.fromEntries(
            Object.entries(state.driverOverrides.updated).filter(([k]) => k !== id)
          ),
        };
      } else {
        // 追加分の削除は added から除去するだけ
        overrides = {
          ...state.driverOverrides,
          added: state.driverOverrides.added.filter((a) => a.id !== id),
        };
      }
      const drivers = computeDrivers(state.sheetMaster, overrides);
      return recalc({ ...state, drivers, driverOverrides: overrides });
    }
    case "ADD_DEDUCTION_ITEM":
      return { ...state, deductionMaster: [...state.deductionMaster, action.item] };
    case "UPDATE_DEDUCTION_ITEM":
      return {
        ...state,
        deductionMaster: state.deductionMaster.map((d) => (d.id === action.item.id ? action.item : d)),
      };
    case "DELETE_DEDUCTION_ITEM":
      return {
        ...state,
        deductionMaster: state.deductionMaster.filter((d) => d.id !== action.id),
      };
    case "ADD_SAGAWA_RULE":
      // 同じ便種コードがあれば上書き、無ければ追加。変更後は再計算（計上区分が変わるため）
      return recalc({
        ...state,
        sagawaBenkindRules: state.sagawaBenkindRules.some((r) => r.code === action.rule.code)
          ? state.sagawaBenkindRules.map((r) => (r.code === action.rule.code ? action.rule : r))
          : [...state.sagawaBenkindRules, action.rule],
      });
    case "UPDATE_SAGAWA_RULE":
      return recalc({
        ...state,
        sagawaBenkindRules: state.sagawaBenkindRules.map((r) => (r.code === action.rule.code ? action.rule : r)),
      });
    case "DELETE_SAGAWA_RULE":
      return recalc({
        ...state,
        sagawaBenkindRules: state.sagawaBenkindRules.filter((r) => r.code !== action.code),
      });
    case "SET_SHIDO_RULES":
      return recalc({ ...state, shidoRules: action.rules });
    case "SET_OFFICE_SUM_RULES":
      return recalc({ ...state, officeSumRules: action.rules });
    case "SET_GROUP_RULES":
      // 取込CSVが無いとき（再オープン中など）に recalc すると、スナップショットの明細が
      // ほぼ全消えする（byDriver が空になる）ため、設定値だけを差し替える
      if (Object.keys(state.imports).length === 0) return { ...state, groupRules: action.rules };
      return recalc({ ...state, groupRules: action.rules });
    case "ADD_GROUP_MEMBER": {
      const name = action.name.trim();
      if (!name) return state;
      const target = state.groupRules.find((r) => r.id === action.ruleId);
      if (!target) return state;
      if (target.memberNames.some((n) => normalizeName(n) === normalizeName(name))) return state; // 既に居る
      const groupRules = state.groupRules.map((r) =>
        r.id === action.ruleId ? { ...r, memberNames: [...r.memberNames, name] } : r
      );
      if (Object.keys(state.imports).length === 0) return { ...state, groupRules };
      return recalc({ ...state, groupRules });
    }
    case "ADD_MANUAL_STATEMENT": {
      if (state.manualDriverIds.includes(action.driverId)) return state;
      return recalc({
        ...state,
        manualDriverIds: [...state.manualDriverIds, action.driverId],
        // 手動作成＝通知書を出したい意思表示なので、配信除外は自動解除する
        excludedDriverIds: state.excludedDriverIds.filter((id) => id !== action.driverId),
      });
    }
    case "REMOVE_MANUAL_STATEMENT": {
      // 手動明細を削除（明細本体は recalc で消える。調整等の引き継ぎ情報も statements から消える）
      const manualDriverIds = state.manualDriverIds.filter((id) => id !== action.driverId);
      const statements = state.statements.filter((s) => s.driverId !== action.driverId);
      // 取込CSVが無いとき（再オープン中など）に recalc すると、スナップショットの明細が
      // ほぼ全消えする（byDriver が manualDriverIds からしか作られない）ため、対象行の削除だけ行う
      if (Object.keys(state.imports).length === 0) return { ...state, manualDriverIds, statements };
      return recalc({ ...state, manualDriverIds, statements });
    }
    case "EXCLUDE_DRIVER": {
      // 既に除外済みでも早期 return しない：旧クライアントの共有保存などで
      // 「除外リストに居るのに0円明細が残っている」矛盾状態になり得るため、冪等な修復として下を通す
      const excludedDriverIds = state.excludedDriverIds.includes(action.driverId)
        ? state.excludedDriverIds
        : [...state.excludedDriverIds, action.driverId];
      if (Object.keys(state.imports).length > 0) return recalc({ ...state, excludedDriverIds });
      // 取込CSVが無いとき（再オープン中など）に recalc すると明細が全消えするため、
      // 対象の0円明細だけをその場で落とす（0円でなければ残す＝支払を消さない）
      const statements = state.statements.filter(
        (s) =>
          s.driverId !== action.driverId ||
          !(s.salesTotal === 0 && s.deductTotal === 0 && s.netTotal === 0)
      );
      return { ...state, excludedDriverIds, statements };
    }
    case "RESTORE_EXCLUDED_DRIVER": {
      const excludedDriverIds = state.excludedDriverIds.filter((id) => id !== action.driverId);
      // 取込CSVがあれば再計算で通知書が復活する。無ければリストから外すだけ
      // （0円明細は次回のCSV取込時に再生成される）
      if (Object.keys(state.imports).length > 0) return recalc({ ...state, excludedDriverIds });
      return { ...state, excludedDriverIds };
    }
    case "SET_ADJUSTMENTS": {
      const statements = state.statements.map((s) =>
        s.driverId === action.driverId ? applyAdjustments(s, action.adjustments) : s
      );
      return { ...state, statements };
    }
    case "LOAD_ARCHIVE_FOR_EDIT": {
      // 過去月の確定スナップショットを「作業中」に読み込む（再オープン＝訂正）。
      // 生CSVは保存していないため imports/matches は空。明細は snapshot をそのまま編集対象にする
      // （recalc は走らせない＝単価マスターからの再計算ではなく、確定済みの数値を維持して補正する）。
      const r = action.record;
      return {
        ...state,
        targetMonth: r.month,
        statements: r.statements,
        imports: {},
        matches: {},
        // 再オープン＝訂正すると決めた操作なので、そのまま編集できる状態で開く
        editUnlocked: true,
        unlockedBy: action.by || null,
        unlockedAt: new Date().toISOString(),
        deductionMaster: r.deductionMaster && r.deductionMaster.length > 0 ? r.deductionMaster : state.deductionMaster,
      };
    }
    case "LOAD_SHARED": {
      // 共有された作業中データを「そのまま」採用する（再計算しない）。
      //
      // ⚠️ 受け取った側で recalc すると、マスターの取得状況が端末ごとに違うとき
      //    （初回起動でシート未取得＝masters.json フォールバック中など）別の数字になり、
      //    それを共有へ押し戻して全員の内容を壊しうる。
      //    共有された明細＝作った人の作業結果を正とし、再計算は「取込・マスター更新・
      //    設定変更」を行った端末でだけ起きるようにする。
      const p = action.payload;
      const driverOverrides = p.driverOverrides || EMPTY_OVERRIDES;
      return {
        ...state,
        imports: p.imports || {},
        targetMonth: p.targetMonth,
        statements: p.statements || [],
        matches: p.matches || {},
        driverOverrides,
        // ドライバー一覧はこの端末のマスター取得結果から作る（明細の数字には影響しない）
        drivers: computeDrivers(state.sheetMaster, driverOverrides),
        // ?? でフォールバックする（`length ? :` にすると「相手が全件削除した」が伝わらず、
        // 消したはずの項目が受信側に残り、受信側の保存で削除が巻き戻る）
        deductionMaster: p.deductionMaster ?? state.deductionMaster,
        sagawaBenkindRules: p.sagawaBenkindRules ?? state.sagawaBenkindRules,
        shidoRules: p.shidoRules ?? state.shidoRules,
        officeSumRules: p.officeSumRules ?? state.officeSumRules,
        groupRules: p.groupRules ?? state.groupRules,
        manualDriverIds: p.manualDriverIds || [],
        // 設定扱い（旧クライアントの保存データに無ければ手元の値を維持＝巻き戻さない）
        excludedDriverIds: p.excludedDriverIds ?? state.excludedDriverIds,
        // 訂正モードは全員で揃える（誰かが訂正を始めたら、他の人の画面も編集可能になる）
        // 旧バンドルの端末（キー自体が無い保存）で訂正モードが黙って解除されないよう、
        // キーが無いときは手元の値を維持する（excludedDriverIds と同じ扱い）
        editUnlocked: p.editUnlocked ?? state.editUnlocked,
        unlockedBy: p.unlockedBy ?? state.unlockedBy,
        unlockedAt: p.unlockedAt ?? state.unlockedAt,
      };
    }
    case "SET_FINALIZED_MONTHS":
      return { ...state, finalizedMonths: action.months };
    case "UNLOCK_MONTH":
      // 訂正モードに入る（＝確定した月をあえて編集可能にする）。誰がいつ始めたかを残す
      return { ...state, editUnlocked: true, unlockedBy: action.by, unlockedAt: new Date().toISOString() };
    case "RELOCK_MONTH":
      return { ...state, editUnlocked: false, unlockedBy: null, unlockedAt: null };
    case "CLEAR_ALL":
      return {
        finalizedMonths: state.finalizedMonths,
        editUnlocked: false,
        unlockedBy: null,
        unlockedAt: null,
        drivers: state.drivers,
        driverOverrides: state.driverOverrides,
        sheetMaster: state.sheetMaster, // マスター同期状態は消さない
        imports: {},
        targetMonth: defaultTargetMonth({}),
        statements: [],
        matches: {},
        deductionMaster: state.deductionMaster, // マスターは消さない
        sagawaBenkindRules: state.sagawaBenkindRules, // 便種マッピングも消さない
        shidoRules: state.shidoRules, // 指導料設定も消さない
        officeSumRules: state.officeSumRules, // 営業所集計も消さない
        groupRules: state.groupRules, // グループ集計設定も消さない
        manualDriverIds: [], // 手動明細は作業データなので消す
        excludedDriverIds: state.excludedDriverIds, // 配信除外は設定なので消さない（毎月やり直しさせない）
      };
    case "HYDRATE": {
      // localStorage から復元。imports があれば最新ロジックで再計算する
      // （計算ロジック変更後でも、復元時に最新数値に揃う）
      const merged = { ...state, ...action.partial };
      // overrides / sheetMaster が復元された場合は drivers を再合成
      if (action.partial.driverOverrides || action.partial.sheetMaster) {
        merged.drivers = computeDrivers(merged.sheetMaster, merged.driverOverrides);
      }
      if (Object.keys(merged.imports).length > 0) {
        return recalc(merged);
      }
      return merged;
    }
    default:
      return state;
  }
}

const initialState: State = {
  drivers: masters.drivers,
  driverOverrides: EMPTY_OVERRIDES,
  sheetMaster: null,
  imports: {},
  targetMonth: defaultTargetMonth({}),
  statements: [],
  matches: {},
  deductionMaster: DEFAULT_DEDUCTION_MASTER,
  sagawaBenkindRules: DEFAULT_SAGAWA_BENKIND_RULES,
  shidoRules: DEFAULT_SHIDO_RULES,
  officeSumRules: DEFAULT_OFFICE_SUM_RULES,
  groupRules: DEFAULT_GROUP_RULES,
  manualDriverIds: [],
  excludedDriverIds: [],
  finalizedMonths: [],
  editUnlocked: false,
  unlockedBy: null,
  unlockedAt: null,
};

// ===== localStorage 永続化ヘルパー =====

type Persisted = {
  version: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  imports: State["imports"];
  targetMonth: string;
  statements: Statement[];
  matches: State["matches"];
  driverOverrides?: DriverOverrides; // v2 から
  deductionMaster?: DeductionMasterItem[]; // v3 から
  sheetMaster?: SheetMaster | null; // v4 から（最後に取得したマスター写し＝オフライン時のスナップショット）
  sagawaBenkindRules?: SagawaBenkindRule[]; // v5 から（佐川 便種マッピング）
  shidoRules?: ShidoRule[]; // v6 から（指導料設定）
  officeSumRules?: OfficeSumRule[]; // v6 から（営業所コード集計）
  manualDriverIds?: string[]; // v6 から（手動作成した通知書）
  groupRules?: GroupRule[]; // v7 から（グループ集計。docs/33）
  // 0円通知書の配信除外（本城さん 2026-08-10）。optional 追加のみで version は上げない
  // （version を上げると、デプロイ跨ぎで旧バンドルが localStorage を「未知の版」として全捨てするため）
  excludedDriverIds?: string[];
  // v8 から（作業中データの共有）。最後にサーバと一致していた版と、その内容の指紋。
  // 「この端末に、まだ共有へ送っていない変更があるか」を再起動後も判定するために持つ。
  syncRev?: number | null;
  syncToken?: string | null;
  syncHash?: string | null;
  // 確定した月のロック（本城さん要望 2026-09-01）。optional 追加のみで version は上げない。
  // finalizedMonths を持たせるのは、月インデックスをサーバから取り直すまでの間も
  // ロックを効かせるため（Fableレビュー指摘: 取得前にマスター再取得の再計算が走ると
  // 確定済みの金額が動き、そのまま共有へ保存されうる）
  finalizedMonths?: string[];
  editUnlocked?: boolean;
  unlockedBy?: string | null;
  unlockedAt?: string | null;
  savedAt: string;
};

function loadPersisted(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    if (![1, 2, 3, 4, 5, 6, 7, 8].includes(parsed.version)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePersisted(state: State, sync: { rev: number | null; token: string | null; hash: string | null }): void {
  try {
    const data: Persisted = {
      version: 8,
      imports: state.imports,
      targetMonth: state.targetMonth,
      statements: state.statements,
      matches: state.matches,
      driverOverrides: state.driverOverrides,
      deductionMaster: state.deductionMaster,
      sheetMaster: state.sheetMaster,
      sagawaBenkindRules: state.sagawaBenkindRules,
      shidoRules: state.shidoRules,
      officeSumRules: state.officeSumRules,
      manualDriverIds: state.manualDriverIds,
      groupRules: state.groupRules,
      excludedDriverIds: state.excludedDriverIds,
      syncRev: sync.rev,
      syncToken: sync.token,
      syncHash: sync.hash,
      finalizedMonths: state.finalizedMonths,
      editUnlocked: state.editUnlocked,
      unlockedBy: state.unlockedBy,
      unlockedAt: state.unlockedAt,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // QuotaExceededError 等
    console.warn("[store] localStorage 保存失敗:", e);
  }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ===== 作業中データの共有（本城さん要望 2026-08-06）=====
//
// 事務所の全員が「同じ作業中の画面」を見られるようにする。
// これまで作業中データは各PCの localStorage にしか無く、本城さんが取り込んだ月の内容が
// 天羽さんの画面には出なかった（docs/29 の設計どおりの挙動）。
//
// 方針:
//   - サーバ（KV）に「いま全員で作業している状態」を1つ置く
//   - 変更が止まって SHARE_SAVE_DEBOUNCE_MS 経ったら自動保存（打鍵ごとには送らない）
//   - 見ているだけの人（手元に変更が無い人）は、他の人の更新を自動で取り込む
//   - 手元に変更がある状態で他の人の更新に気づいたら、勝手に決めずに人へ問う
//     （＝検出は自動、判断は人間）
const SHARE_SAVE_DEBOUNCE_MS = 8000;
const SHARE_POLL_MS = 20000;

// 共有する作業中データ。sheetMaster は含めない（各PCが起動時に正本シートから取得するため、
// 取得時刻の差だけで「変更あり」と誤判定され、無意味な保存が往復してしまう）。
type SharedWork = {
  schema: 1;
  imports: State["imports"];
  targetMonth: string;
  statements: Statement[];
  matches: State["matches"];
  driverOverrides: DriverOverrides;
  deductionMaster: DeductionMasterItem[];
  sagawaBenkindRules: SagawaBenkindRule[];
  shidoRules: ShidoRule[];
  officeSumRules: OfficeSumRule[];
  groupRules: GroupRule[];
  manualDriverIds: string[];
  excludedDriverIds?: string[]; // 旧クライアントの保存データには無い（optional）
  // 確定した月の訂正モード（本城さん要望 2026-09-01）。optional 追加のみで schema は上げない
  editUnlocked?: boolean;
  unlockedBy?: string | null;
  unlockedAt?: string | null;
};

export function serializeShared(state: State): string {
  const payload: SharedWork = {
    schema: 1,
    imports: state.imports,
    targetMonth: state.targetMonth,
    statements: state.statements,
    matches: state.matches,
    driverOverrides: state.driverOverrides,
    deductionMaster: state.deductionMaster,
    sagawaBenkindRules: state.sagawaBenkindRules,
    shidoRules: state.shidoRules,
    officeSumRules: state.officeSumRules,
    groupRules: state.groupRules,
    manualDriverIds: state.manualDriverIds,
    excludedDriverIds: state.excludedDriverIds,
    editUnlocked: state.editUnlocked,
    unlockedBy: state.unlockedBy,
    unlockedAt: state.unlockedAt,
  };
  return JSON.stringify(payload);
}

// 復元した内容（再計算前）に「人の編集」が無いか（本城さん報告 2026-09-09）。
// 最後に共有と一致していた指紋（syncHash）と、localStorage に残っていた内容の指紋が同じなら、
// 前回の同期のあと誰もこの端末で編集していない。復元時の再計算で数値が変わっても、それは
// 改修後のロジック・最新マスターによる計算し直しであって「未保存の変更」ではないので、
// 起動時は共有の最新を取り込んでよい。
export function persistedHasNoUserEdits(syncHash: string | null | undefined, partial: Partial<State>): boolean {
  if (syncHash == null) return false;
  return syncHash === fingerprint(serializeShared({ ...initialState, ...partial } as State));
}

// 内容の指紋（FNV-1a）。数MBの本文をそのまま localStorage に持てないため、
// 「前回サーバと一致していた内容かどうか」の比較にはこの短い文字列を使う。
export function fingerprint(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16) + ":" + s.length.toString(16);
}

export type ShareMode = "init" | "off" | "synced" | "saving" | "error";

export type ShareInfo = {
  mode: ShareMode;
  rev: number | null;
  savedAt: string | null;
  savedBy: string | null;
  error: string | null;
};

// 人の判断が要る状態:
//   remote      … 他の人が更新した（手元にも未保存の変更がある）
//   startup     … 起動時、サーバの内容と手元の内容が食い違っていた
//   empty       … 明細0件で、明細のある共有データを上書きしようとした（ガード作動）
//   overwritten … 同時保存で、この端末の保存が他の人の保存に上書きされた
export type ShareConflict = { reason: "remote" | "startup" | "empty" | "overwritten"; meta: WorkMeta | null } | null;

// ===== Context =====

const Ctx = createContext<{
  state: State;
  dispatch: React.Dispatch<Action>;
  clearAll: () => void;
  hasUnsavedWork: boolean;
  // マスターシート同期（Phase 1）
  refreshMaster: () => Promise<void>;
  masterLoading: boolean;
  masterError: string | null;
  // 月別アーカイブ（docs/29）
  monthIndex: MonthIndexEntry[];
  archivedRecord: MonthlyRecord | null; // 過去月を閲覧中なら、その読み取りレコード
  isArchive: boolean; // 過去月（確定済み）を閲覧中か
  viewingMonth: string; // 現在表示している月（作業中 or 過去月）
  displayStatements: Statement[]; // 画面が表示すべき明細（作業中 or 過去月のスナップショット）
  archiveBusy: boolean;
  refreshMonthIndex: () => Promise<void>;
  // 確定した月のロック（本城さん要望 2026-09-01）
  monthLocked: boolean; // 作業中の月が確定済みで、いま編集できない状態か
  unlockInfo: { by: string | null; at: string | null }; // 訂正モードを始めた人と時刻
  unlockMonth: (by?: string) => void; // 訂正モードに入る（確定済みの月を編集可能にする）
  finalizeMonth: (finalizedBy?: string) => Promise<{ ok: boolean; error?: string }>;
  viewMonth: (month: string) => Promise<{ ok: boolean; error?: string }>;
  exitArchive: () => void;
  reopenMonth: (month: string, reopenedBy?: string) => Promise<{ ok: boolean; error?: string }>;
  // 確定済みの過去月で1人分だけ訂正して保存し直す（作業中の月・他の人には触れない。2026-10-06）
  correctArchived: (stmt: Statement, reason: string) => Promise<{ ok: boolean; error?: string; conflict?: boolean }>;
  // 作業中データの共有（本城さん要望 2026-08-06）
  share: ShareInfo;
  shareConflict: ShareConflict;
  loadShared: () => Promise<{ ok: boolean; error?: string }>; // 共有の最新を取り込む（手元の変更は破棄）
  refreshShared: () => Promise<{ ok: boolean; changed: boolean; error?: string }>; // 「最新の状態にする」（未保存の変更があれば帯で選ばせる）
  overwriteShared: () => Promise<{ ok: boolean; error?: string }>; // 手元の内容で共有を上書きする
} | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // useRef ではなく useState を使う。useRef にすると、HYDRATE dispatch と同じ effect 内で
  // ref を立ててしまい、続く保存 useEffect が「HYDRATE 前の initialState」を save してしまう。
  // useState なら hydrated の変更も React の更新キューに乗り、HYDRATE 反映後のレンダリングで
  // まとめて再評価されるため、save される state は必ず HYDRATE 後の値になる。
  const [hydrated, setHydrated] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState<string | null>(null);
  // 月別アーカイブ
  const [monthIndex, setMonthIndex] = useState<MonthIndexEntry[]>([]);
  const [archivedRecord, setArchivedRecord] = useState<MonthlyRecord | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [monthIndexLoaded, setMonthIndexLoaded] = useState(false); // 月インデックスを一度でも取得できたか

  // ===== 作業中データの共有 =====
  const [share, setShare] = useState<ShareInfo>({ mode: "init", rev: null, savedAt: null, savedBy: null, error: null });
  const [shareConflict, setShareConflict] = useState<ShareConflict>(null);
  const shareOffRef = useRef(false); // 共有が使えない環境（KV未設定・ローカル開発）
  const syncReadyRef = useRef(false); // 初回同期が済むまで保存しない（空データで共有を潰さないため）
  const syncRevRef = useRef<number | null>(null); // いま手元が土台にしているサーバの版
  const syncTokenRef = useRef<string | null>(null); // その版の保存トークン（後勝ち上書きの検出用）
  const syncHashRef = useRef<string | null>(null); // その版と一致していた内容の指紋
  const initBusyRef = useRef(false); // 起動時同期の多重実行よけ
  const pendingSaveRef = useRef(false); // 保存中に発生した変更（完了後に保存し直す）
  const skipNextSaveRef = useRef(false); // 取り込み直後の1回は保存しない（保存の往復を止める）
  const forceNextSaveRef = useRef(false); // 人が選んだ上書き（全消去・競合解決）
  const savingRef = useRef(false);
  // 起動時に「この端末には人の編集が無い」と分かっているか（本城さん報告 2026-09-09）。
  // 復元時の再計算（改修後のロジック・マスター再取得）で内容の指紋が変わると、誰も触っていないのに
  // 「手元に未保存の変更あり」と誤判定され、共有の最新を取り込まずに古い内容を表示し続けていた。
  // 復元直前の保存内容が最後の同期時の指紋と一致していれば、人の編集は無いと判断してよい。
  const hydratedCleanRef = useRef(false);
  // hydrate 後に人の操作（画面からの dispatch）で変更があったか。内部の再計算は数えない
  const userEditedRef = useRef(false);
  // 直近の state をタイマー・イベントから参照するための写し
  const stateRef = useRef(state);
  // 同じく share / shareConflict の写し（ポーリングから「保存に失敗したまま」を判定するため）
  const shareRef = useRef(share);
  shareRef.current = share;
  const shareConflictRef = useRef(shareConflict);
  shareConflictRef.current = shareConflict;
  stateRef.current = state;

  // 起動時：localStorage から復元
  useEffect(() => {
    const p = loadPersisted();
    if (p) {
      syncRevRef.current = p.syncRev ?? null;
      syncTokenRef.current = p.syncToken ?? null;
      syncHashRef.current = p.syncHash ?? null;
      const partial: Partial<State> = {
          imports: p.imports || {},
          targetMonth: p.targetMonth,
          statements: p.statements || [],
          matches: p.matches || {},
          driverOverrides: p.driverOverrides || EMPTY_OVERRIDES,
          sheetMaster: p.sheetMaster || null,
          // 旧バージョン（v1/v2）には無いので初期マスターで補完
          deductionMaster:
            p.deductionMaster && p.deductionMaster.length > 0
              ? p.deductionMaster
              : DEFAULT_DEDUCTION_MASTER,
          // v4以前には無いので既定マッピングで補完
          sagawaBenkindRules:
            p.sagawaBenkindRules && p.sagawaBenkindRules.length > 0
              ? p.sagawaBenkindRules
              : DEFAULT_SAGAWA_BENKIND_RULES,
          // v5以前には無いので初期設定（本城さん 6/30 確定情報）で補完
          shidoRules: p.shidoRules && p.shidoRules.length > 0 ? p.shidoRules : DEFAULT_SHIDO_RULES,
          officeSumRules:
            p.officeSumRules && p.officeSumRules.length > 0 ? p.officeSumRules : DEFAULT_OFFICE_SUM_RULES,
          // v6以前には無いので初期設定（AT-STYLE様プリセット）で補完
          groupRules: p.groupRules && p.groupRules.length > 0 ? p.groupRules : DEFAULT_GROUP_RULES,
          manualDriverIds: p.manualDriverIds || [],
          // v8以前には無いので空で補完
          excludedDriverIds: p.excludedDriverIds || [],
          // 確定した月のロック（本城さん要望 2026-09-01）。
          // 旧データには無いので、ロックする側（編集不可・訂正モードでない）に倒す
          finalizedMonths: p.finalizedMonths || [],
          editUnlocked: p.editUnlocked ?? false,
          unlockedBy: p.unlockedBy ?? null,
          unlockedAt: p.unlockedAt ?? null,
      };
      // 復元する内容（再計算前）が、最後に共有と一致していた内容と同じか。
      // 同じなら、この端末に人の編集は無い（＝再計算で数値が動いても「未保存の変更」ではない）
      const preClean = persistedHasNoUserEdits(p.syncHash, partial);
      hydratedCleanRef.current = preClean;
      if (preClean) {
        // 再計算後の内容を「共有と一致している」ものとして記録し直す（送り返さない）
        skipNextSaveRef.current = true;
      }
      dispatch({ type: "HYDRATE", partial });
    }
    setHydrated(true);
  }, []);

  // マスターシートから最新を取得（失敗時は前回取得分 or masters.json のまま動く）
  const refreshMaster = async () => {
    setMasterLoading(true);
    try {
      const r = await fetchSheetMaster();
      if (r.ok) {
        dispatch({ type: "SET_SHEET_MASTER", data: r.data });
        setMasterError(null);
      } else {
        // 未設定（configured:false）はエラー扱いにしない＝ローカル運用を許容
        setMasterError(r.configured ? r.error : null);
      }
    } finally {
      setMasterLoading(false);
    }
  };

  // 起動時に1回 自動取得（hydrate 完了後）
  useEffect(() => {
    if (!hydrated) return;
    void refreshMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // 状態変更時：localStorage に自動保存（hydrate 完了後のみ）
  const persistNow = () =>
    savePersisted(stateRef.current, { rev: syncRevRef.current, token: syncTokenRef.current, hash: syncHashRef.current });
  useEffect(() => {
    if (!hydrated) return;
    persistNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, hydrated]);

  // ===== 作業中データの共有（本城さん要望 2026-08-06）=====

  // サーバの内容を手元に取り込む
  const applyShared = (
    json: string,
    rev: number,
    token: string | null,
    savedAt: string | null,
    savedBy: string | null
  ): boolean => {
    let p: SharedWork;
    try {
      p = JSON.parse(json) as SharedWork;
    } catch {
      return false;
    }
    // 形が読めないものは採用しない（壊れたデータで手元を潰さない）
    if (!p || typeof p !== "object") return false;
    if (!Array.isArray(p.statements) || typeof p.targetMonth !== "string") return false;
    skipNextSaveRef.current = true;
    dispatch({ type: "LOAD_SHARED", payload: p });
    syncRevRef.current = rev;
    syncTokenRef.current = token;
    syncHashRef.current = fingerprint(json);
    setShare({ mode: "synced", rev, savedAt, savedBy, error: null });
    setShareConflict(null);
    return true;
  };

  // 手元に未保存の変更が無いか（起動時・取り込み前の判定に使う）
  const isLocalClean = (): boolean =>
    syncHashRef.current != null && syncHashRef.current === fingerprint(serializeShared(stateRef.current));

  // requireClean を渡すと、取得（数MBのダウンロードで数秒かかる）の間に編集が入った場合は
  // 取り込まずに人へ確認を出す。渡さないと、その間の編集が黙って巻き戻る。
  const pullShared = async (opts?: { requireClean?: boolean; conflictMeta?: WorkMeta | null }): Promise<{ ok: boolean; error?: string }> => {
    const r = await fetchWorkDoc();
    if (!r.ok || !r.json) return { ok: false, error: r.error || "共有データを取得できませんでした" };
    if (opts?.requireClean && !isLocalClean()) {
      setShareConflict({ reason: "remote", meta: opts.conflictMeta ?? null });
      setShare((s) => ({ ...s, mode: "error", error: "取得中にこの端末で編集がありました" }));
      return { ok: false, error: "取得中に編集がありました" };
    }
    const ok = applyShared(r.json, r.rev ?? 0, r.token ?? null, r.savedAt ?? null, r.savedBy ?? null);
    return ok ? { ok: true } : { ok: false, error: "共有データの形式が読めませんでした" };
  };

  const pushShared = async (opts?: { force?: boolean }): Promise<{ ok: boolean; error?: string }> => {
    if (shareOffRef.current) return { ok: false, error: "共有は無効です" };
    if (savingRef.current) {
      // 保存中に発火した分は捨てない。完了後に必ずもう一度保存する
      // （捨てると「共有中（緑）」の表示のまま手元の変更が共有されないまま残る）
      pendingSaveRef.current = true;
      return { ok: false, error: "保存中です" };
    }
    const st = stateRef.current;
    const json = serializeShared(st);
    const hash = fingerprint(json);
    // force は「人がその1回の操作として選んだ上書き」。読んだ時点で消費する
    // （成功時のみ消すと、失敗して残ったフラグが後日の無関係な自動保存をガード素通りさせる）
    const force = opts?.force === true || forceNextSaveRef.current;
    forceNextSaveRef.current = false;
    if (!force && hash === syncHashRef.current) return { ok: true }; // 内容が変わっていない
    savingRef.current = true;
    setShare((s) => ({ ...s, mode: "saving", error: null }));
    try {
      const r = await saveWorkDoc(json, {
        month: st.targetMonth,
        count: st.statements.length,
        baseRev: syncRevRef.current,
        force,
      });
      if (r.ok && r.meta) {
        syncRevRef.current = r.meta.rev;
        syncTokenRef.current = r.meta.token || null;
        syncHashRef.current = hash;
        setShare({ mode: "synced", rev: r.meta.rev, savedAt: r.meta.savedAt, savedBy: r.meta.savedBy, error: null });
        setShareConflict(null);
        persistNow();
        return { ok: true };
      }
      if (r.reason === "conflict" || r.reason === "empty") {
        // 上書きせずに止める。どちらを採るかは人が選ぶ
        setShareConflict({ reason: r.reason === "empty" ? "empty" : "remote", meta: r.meta ?? null });
        setShare((s) => ({ ...s, mode: "error", error: "共有に保存していません（他の方が更新）" }));
        return { ok: false, error: "他の方が先に更新しています" };
      }
      setShare((s) => ({ ...s, mode: "error", error: r.error || "共有への保存に失敗しました" }));
      return { ok: false, error: r.error };
    } finally {
      savingRef.current = false;
      if (pendingSaveRef.current) {
        // 保存中に発生した変更を保存し直す（内容が変わっていなければ中で早期に戻る）
        pendingSaveRef.current = false;
        window.setTimeout(() => void pushShared(), 0);
      }
    }
  };

  // 起動時の同期。ここで「取り込む／送る／人に聞く」を決める。
  // 通信失敗で終わったときは syncReady が立たないため、以降のポーリングから再試行される。
  const initSync = async (): Promise<void> => {
    if (initBusyRef.current) return;
    initBusyRef.current = true;
    try {
      const m = await fetchWorkMeta();
      if (m.reachable && !m.configured) {
        // KV未設定・API未提供（ローカル開発）→ 共有なしで従来どおりこの端末だけで動く。
        // 恒久的にオフにするのはこの場合だけ（通信エラーと区別する）
        shareOffRef.current = true;
        setShare({ mode: "off", rev: null, savedAt: null, savedBy: null, error: null });
        return;
      }
      if (!m.ok) {
        // 一時的な通信・サーバエラー。共有はオフにせず、次のポーリングでやり直す
        setShare((s) => ({ ...s, mode: "error", error: m.error || "共有サーバに接続できません（自動で再試行します）" }));
        return;
      }
      const meta = m.meta;
      const st = stateRef.current;
      const localEmpty = st.statements.length === 0 && Object.keys(st.imports).length === 0;
      // 指紋が一致していなくても、復元時に人の編集が無いと分かっていて、その後も人が触っていなければ
      // 「未保存の変更なし」として扱う（再計算で指紋が動いただけ。本城さん報告 2026-09-09）
      const hashClean = isLocalClean();
      const localClean = hashClean || (hydratedCleanRef.current && !userEditedRef.current);

      if (!meta) {
        // 共有の初回。手元の内容をそのまま共有の起点にする（本城さんの作業中データを失わないため）
        syncRevRef.current = null;
        syncReadyRef.current = true;
        setShare({ mode: "synced", rev: null, savedAt: null, savedBy: null, error: null });
        if (!localEmpty) await pushShared();
        return;
      }
      if (localEmpty || (localClean && meta.rev !== syncRevRef.current)) {
        // 手元は空 or 手元に未送信の変更なし → サーバの最新をそのまま表示する
        const r = await pullShared({ requireClean: !localEmpty && hashClean, conflictMeta: meta });
        if (r.ok) syncReadyRef.current = true;
        else setShare((s) => ({ ...s, mode: "error", error: r.error || null }));
        return;
      }
      if (localClean) {
        // 既に一致している（指紋が違うだけなら、再計算後の内容を一致点として記録し直す）
        if (!hashClean) syncHashRef.current = fingerprint(serializeShared(stateRef.current));
        syncReadyRef.current = true;
        syncTokenRef.current = meta.token || null;
        setShare({ mode: "synced", rev: meta.rev, savedAt: meta.savedAt, savedBy: meta.savedBy, error: null });
        return;
      }
      if (syncRevRef.current != null && meta.rev === syncRevRef.current) {
        // 手元だけ進んでいる（保存が終わる前に閉じた等）→ 手元の内容を共有へ送る
        syncReadyRef.current = true;
        await pushShared();
        return;
      }
      // 双方が進んでいる → 勝手に決めず人に選ばせる（この間は自動保存しない）
      setShare({ mode: "error", rev: meta.rev, savedAt: meta.savedAt, savedBy: meta.savedBy, error: "共有と手元の内容が違います" });
      setShareConflict({ reason: "startup", meta });
    } finally {
      initBusyRef.current = false;
    }
  };

  useEffect(() => {
    if (!hydrated) return;
    void initSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // 変更が止まったら共有へ自動保存
  useEffect(() => {
    if (!hydrated || shareOffRef.current) return;
    if (skipNextSaveRef.current) {
      // 取り込み直後。再計算まで含めた「いまの内容」をサーバと一致しているものとして記録し、
      // 送り返さない（2台で保存が往復するのを止める）
      skipNextSaveRef.current = false;
      syncHashRef.current = fingerprint(serializeShared(state));
      persistNow();
      return;
    }
    if (!syncReadyRef.current) return;
    const t = window.setTimeout(() => {
      void pushShared();
    }, SHARE_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, hydrated]);

  // 他の人の更新を拾う。見ているだけの人は自動で追従し、手元に変更がある人には確認を出す。
  useEffect(() => {
    if (!hydrated) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || shareOffRef.current || savingRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      if (!syncReadyRef.current) {
        // 起動時の同期が通信エラーで終わっていた → ここでやり直す
        await initSync();
        return;
      }
      const m = await fetchWorkMeta();
      if (stopped || !m.ok) return;
      if (!m.meta) {
        // 共有がまだ空。初回保存（initSync の pushShared）が通信失敗したままなら、ここで保存し直す
        // （Astra レビュー R3 2026-09-07: 以前は meta:null で早期 return し、編集しない限り再送されなかった）。
        // force は付けない＝サーバ側の版チェック・0件ガードはそのまま通る。競合（人の判断待ち）は触らない
        if (shareRef.current.mode === "error" && !shareConflictRef.current && syncRevRef.current == null && !isLocalClean()) {
          await pushShared();
        }
        return;
      }
      // 月の確定状況も拾う（作業データだけ同期されて「確定済み」が古いままになるのを防ぐ）
      void refreshMonthIndex();
      const sameRev = m.meta.rev === syncRevRef.current;
      // 版が同じでも保存トークンが変わっていたら、自分の保存が他の人の保存に上書きされている
      // （KV に条件付き書込が無いため、同時保存では両方が同じ版番号を採る）
      const overwritten = sameRev && !!syncTokenRef.current && !!m.meta.token && m.meta.token !== syncTokenRef.current;
      if (sameRev && !overwritten) {
        // サーバは進んでいないが、手元の保存が失敗したまま（通信エラー・一時的なサーバエラー）
        // → ここで保存し直す。以前は「次に何か操作するまで」保存し直さなかったため、
        //   作業を終えて閉じると共有に届かないまま残った（2026-09-07 本城さん報告の一因）。
        //   競合（人の判断待ち）のときは触らない。
        if (shareRef.current.mode === "error" && !shareConflictRef.current && !isLocalClean()) {
          await pushShared();
        }
        return;
      }
      if (isLocalClean()) {
        await pullShared({ requireClean: true, conflictMeta: m.meta });
      } else {
        setShareConflict({ reason: overwritten ? "overwritten" : "remote", meta: m.meta });
        setShare((s) => ({ ...s, mode: "error", error: overwritten ? "この端末の保存が上書きされました" : "他の方が更新しました" }));
      }
    };
    const id = window.setInterval(() => void tick(), SHARE_POLL_MS);
    const onFocus = () => void tick();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopped = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // 共有の最新を取り込む（手元の変更は破棄される）
  const loadShared = async (): Promise<{ ok: boolean; error?: string }> => {
    const r = await pullShared();
    if (r.ok) syncReadyRef.current = true;
    return r;
  };

  // 「最新の状態にする」ボタン（本城さん要望 2026-09-09）。
  // 共有の版を確かめ、手元に未保存の変更が無ければ最新を取り込む。未保存の変更があるときは
  // 黙って捨てず、従来どおり帯で「共有の最新を表示する／この端末の内容で上書き」を選んでもらう。
  // あわせて月の確定状況（保存済みデータの一覧）も取り直す。
  const refreshShared = async (): Promise<{ ok: boolean; changed: boolean; error?: string }> => {
    if (shareOffRef.current) return { ok: true, changed: false };
    void refreshMonthIndex();
    if (!syncReadyRef.current) {
      await initSync();
      return { ok: shareRef.current.mode !== "error", changed: true, error: shareRef.current.error || undefined };
    }
    const m = await fetchWorkMeta();
    if (!m.ok) return { ok: false, changed: false, error: m.error || "共有サーバに接続できません" };
    if (!m.meta) return { ok: true, changed: false };
    const sameRev = m.meta.rev === syncRevRef.current;
    const overwritten = sameRev && !!syncTokenRef.current && !!m.meta.token && m.meta.token !== syncTokenRef.current;
    if (sameRev && !overwritten) return { ok: true, changed: false };
    if (isLocalClean()) {
      const r = await pullShared({ requireClean: true, conflictMeta: m.meta });
      return { ok: r.ok, changed: r.ok, error: r.error };
    }
    setShareConflict({ reason: overwritten ? "overwritten" : "remote", meta: m.meta });
    setShare((s) => ({ ...s, mode: "error", error: overwritten ? "この端末の保存が上書きされました" : "他の方が更新しました" }));
    return { ok: false, changed: false, error: "この端末に未保存の変更があります。画面上の帯からどちらを残すか選んでください" };
  };

  // 画面からの dispatch（人の操作）。起動時の「人の編集なし」判定に使う
  const userDispatch = (action: Action) => {
    userEditedRef.current = true;
    dispatch(action);
  };

  // 手元の内容で共有を上書きする（相手の更新は失われる）
  const overwriteShared = async (): Promise<{ ok: boolean; error?: string }> => {
    forceNextSaveRef.current = true;
    syncReadyRef.current = true;
    const r = await pushShared({ force: true });
    if (!r.ok) forceNextSaveRef.current = false;
    return r;
  };

  // 作業中の月がまだ「確定（アーカイブ保存）」されていないか。
  // ヘッダの自動保存ピル（緑=確定済み／アンバー=作業中）に使う。
  // データ自体は localStorage に常時自動保存されるため、離脱警告は出さない。
  // 確定した月のロック（本城さん要望 2026-09-01）。判定は reducer と同じ関数を使う
  const monthLocked = isMonthLocked(state);

  const hasUnsavedWork =
    state.statements.length > 0 && !monthIndex.some((e) => e.month === state.targetMonth);

  const clearAll = () => {
    if (typeof window !== "undefined") {
      // 共有が有効なとき、この操作は自分の画面だけでなく全員の画面から消える。
      // 「自分のPCの掃除のつもり」で押されると被害が全員に及ぶので、そこを明示する。
      const shared = !shareOffRef.current;
      // 確定済みの月でも消せる（＝翌月をまっさらに始める手段）。保存済みは消えないことを明示する
      const lockedNote = isMonthLocked(state)
        ? `\n\n（${state.targetMonth} は確定済みです。保存済みのデータは消えず、「保存済みデータ」からいつでも確認できます）\n`
        : "";
      const ok = window.confirm(
        shared
          ? "取込済みのCSV・支払明細をすべて消去します。\n\n⚠️ この内容は全員で共有しています。消去すると、他の方の画面からも同じように消えます。\n（消す前の内容は1世代だけ保管され、設定画面から戻せます）" + lockedNote + "\n\nよろしいですか？"
          : "取込済みのCSV・支払明細をすべて消去します。" + lockedNote + "よろしいですか？"
      );
      if (!ok) return;
    }
    clearPersisted();
    setArchivedRecord(null);
    // 全消去は人が選んだ操作なので、共有側も空で上書きする（0件ガードを1回だけ外す）
    forceNextSaveRef.current = true;
    userEditedRef.current = true;
    dispatch({ type: "CLEAR_ALL" });
  };

  // ===== 月別アーカイブ操作 =====
  const refreshMonthIndex = async () => {
    const r = await fetchMonthIndex();
    if (r.ok) {
      setMonthIndex(r.months);
      setMonthIndexLoaded(true);
    }
  };
  // 確定済みの月を state 側へ写す（reducer がロック判定に使う。本城さん要望 2026-09-01）。
  // サーバから月インデックスを取れるまでは写さない。取得失敗のときに空配列で上書きすると、
  // localStorage から復元したロックが外れてしまうため（Fableレビュー H-1/L-1）。
  useEffect(() => {
    if (!monthIndexLoaded) return;
    const months = monthIndex.map((e) => e.month);
    if (months.length !== state.finalizedMonths.length || months.some((m, i) => m !== state.finalizedMonths[i])) {
      dispatch({ type: "SET_FINALIZED_MONTHS", months });
    }
  }, [monthIndex, monthIndexLoaded, state.finalizedMonths]);

  // 起動時（hydrate後）に月インデックスを取得
  useEffect(() => {
    if (!hydrated) return;
    void refreshMonthIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // 月を確定 → 現在の作業月のスナップショットをサーバ（KV）へ保存
  const finalizeMonth = async (finalizedBy = "ICB"): Promise<{ ok: boolean; error?: string }> => {
    setArchiveBusy(true);
    try {
      const totals = state.statements.reduce(
        (a, s) => { a.salesTotal += s.salesTotal; a.deductTotal += s.deductTotal; a.netTotal += s.netTotal; return a; },
        { count: state.statements.length, salesTotal: 0, deductTotal: 0, netTotal: 0 }
      );
      const nowIso = new Date().toISOString();
      const record: MonthlyRecord = {
        month: state.targetMonth,
        status: "finalized",
        finalizedAt: nowIso,
        finalizedBy,
        statements: state.statements,
        masterSnapshot: state.sheetMaster
          ? { fetchedAt: state.sheetMaster.fetchedAt, sheetTitle: state.sheetMaster.sheetTitle, drivers: state.sheetMaster.drivers }
          : null,
        imports: { yamato: state.imports.yamato?.meta, sagawa: state.imports.sagawa?.meta },
        deductionMaster: state.deductionMaster,
        totals,
        savedAt: nowIso,
        schemaVersion: 1,
      };
      const r = await saveMonthRecord(record);
      if (r.ok && r.entry) {
        const entry = r.entry;
        // 確定したら再びロックする（訂正モードを閉じる。本城さん要望 2026-09-01）。
        // 月インデックスの反映を待たずにロックしたいので、確定した月をその場で入れる
        dispatch({ type: "RELOCK_MONTH" });
        dispatch({
          type: "SET_FINALIZED_MONTHS",
          months: state.finalizedMonths.includes(entry.month)
            ? state.finalizedMonths
            : [...state.finalizedMonths, entry.month],
        });
        setMonthIndex((idx) => [entry, ...idx.filter((e) => e.month !== entry.month)].sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0)));
        return { ok: true };
      }
      return { ok: false, error: r.error };
    } finally {
      setArchiveBusy(false);
    }
  };

  // 過去月を閲覧（読み取り）。作業中の月を選んだら閲覧解除
  const viewMonth = async (month: string): Promise<{ ok: boolean; error?: string }> => {
    if (month === state.targetMonth) { setArchivedRecord(null); return { ok: true }; }
    setArchiveBusy(true);
    try {
      const r = await fetchMonthRecord(month);
      if (r.ok && r.record) { setArchivedRecord(r.record); return { ok: true }; }
      return { ok: false, error: r.error || "この月のデータが見つかりません" };
    } finally {
      setArchiveBusy(false);
    }
  };
  const exitArchive = () => setArchivedRecord(null);

  // 過去月を再オープン（訂正）：確定スナップショットを作業中に読み込み、編集可能にする
  const reopenMonth = async (month: string, reopenedBy = "ICB"): Promise<{ ok: boolean; error?: string }> => {
    let rec = archivedRecord && archivedRecord.month === month ? archivedRecord : null;
    if (!rec) {
      const r = await fetchMonthRecord(month);
      if (!r.ok || !r.record) return { ok: false, error: r.error || "この月のデータが見つかりません" };
      rec = r.record;
    }
    userEditedRef.current = true;
    dispatch({ type: "LOAD_ARCHIVE_FOR_EDIT", record: rec, by: reopenedBy });
    setArchivedRecord(null);
    return { ok: true };
  };

  // 過去月の1人訂正：開いている過去月のレコードの中で、その人の明細だけをサーバ側で差し替える。
  // 作業中の state には一切 dispatch しない（作業中の月が置き換わらないことがこの機能の目的）
  const correctArchived = async (stmt: Statement, reason: string): Promise<{ ok: boolean; error?: string; conflict?: boolean }> => {
    const rec = archivedRecord;
    if (!rec) return { ok: false, error: "過去月を開いていません" };
    if (stmt.targetMonth && stmt.targetMonth !== rec.month) return { ok: false, error: "明細の対象月が開いている月と一致しません" };
    setArchiveBusy(true);
    try {
      const r = await correctArchivedStatement(rec.month, stmt, rec.savedAt, reason);
      if (r.conflict) {
        // 開いた後に別の方が保存し直していた。手元の古い内容で上書きしないよう、最新の保存データを読み直す
        const fresh = await fetchMonthRecord(rec.month);
        if (fresh.ok && fresh.record) setArchivedRecord((cur) => (cur && cur.month === rec.month ? fresh.record : cur));
        return { ok: false, conflict: true, error: r.error };
      }
      if (!r.ok || !r.record || !r.entry) return { ok: false, error: r.error || "保存に失敗しました" };
      const entry = r.entry;
      // 同じ月をまだ開いているときだけ差し替える（保存中に別の月へ移っていたら上書きしない）
      setArchivedRecord((cur) => (cur && cur.month === rec.month ? r.record! : cur));
      setMonthIndex((idx) => idx.map((e) => (e.month === entry.month ? entry : e)));
      return { ok: true };
    } finally {
      setArchiveBusy(false);
    }
  };

  const isArchive = archivedRecord != null;
  const viewingMonth = archivedRecord ? archivedRecord.month : state.targetMonth;
  const displayStatements = archivedRecord ? archivedRecord.statements : state.statements;

  const value = useMemo(
    () => ({
      state, dispatch: userDispatch, clearAll, hasUnsavedWork, refreshMaster, masterLoading, masterError,
      monthIndex, archivedRecord, isArchive, viewingMonth, displayStatements, archiveBusy,
      refreshMonthIndex, finalizeMonth, viewMonth, exitArchive, reopenMonth, correctArchived,
      monthLocked, unlockInfo: { by: state.unlockedBy, at: state.unlockedAt },
      unlockMonth: (by = "ICB") => userDispatch({ type: "UNLOCK_MONTH", by }),
      share, shareConflict, loadShared, overwriteShared, refreshShared,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, hasUnsavedWork, masterLoading, masterError, monthIndex, archivedRecord, archiveBusy, share, shareConflict]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore must be inside StoreProvider");
  return ctx;
}
